'use client';

// Module-level state to cache the token promise
let turnstileTokenPromise: Promise<string> | null = null;
let turnstileLoadedPromise: Promise<void> | null = null;

/**
 * Interface for Turnstile verification response
 */

// Add Turnstile type definition for client side
declare global {
    interface Window {
        turnstile: {
            render: (container: string | HTMLElement, options: any) => string | number;
            reset: (widgetId?: string | number) => void;
            getResponse: (widgetId?: string | number) => string | undefined;
            remove: (widgetId?: string | number) => void;
            execute: (widgetId?: string | number) => void;
            isExpired: (widgetId?: string | number) => boolean;
            ready: (callback: () => void) => void;
        };
        onloadTurnstileCallback?: () => void;
    }
}

/**
 * Ensures that the Turnstile script is loaded and initialized
 */
function ensureTurnstileLoaded(): Promise<void> {
    if (typeof window === 'undefined') {
        return Promise.reject("Not in browser environment");
    }

    // If we already have a promise, return it
    if (turnstileLoadedPromise) {
        return turnstileLoadedPromise;
    }

    turnstileLoadedPromise = new Promise<void>((resolve, reject) => {
        // If turnstile is already available, resolve immediately
        if (window.turnstile) {
            resolve();
            return;
        }

        // Set up a function that will be called when turnstile loads
        const originalCallback = window.onloadTurnstileCallback;
        window.onloadTurnstileCallback = function () {
            // Call the original callback if it exists
            if (originalCallback) originalCallback();
            resolve();
        };

        // Check if the script is already in the document
        const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');

        if (existingScript) {
            // Use polling since the script exists but might not be fully loaded
            const checkInterval = setInterval(() => {
                if (window.turnstile) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 100);

            // Set a timeout to stop checking after 10 seconds
            setTimeout(() => {
                clearInterval(checkInterval);
                reject("Turnstile failed to load after 10 seconds");
                // Reset the promise so future calls will try again
                turnstileLoadedPromise = null;
            }, 10000);
        } else {
            // Script doesn't exist, add it manually
            const script = document.createElement('script');
            // gleiche URL wie TurnstileProvider, damit kein zweiter Cache-Eintrag entsteht
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback&render=explicit';
            script.async = true;
            script.defer = true;

            // Handle loading errors
            script.onerror = () => {
                console.error("Failed to load Turnstile script");
                reject("Failed to load Turnstile script");
                // Reset the promise so future calls will try again
                turnstileLoadedPromise = null;
            };

            document.body.appendChild(script);
        }
    });

    return turnstileLoadedPromise;
}

/**
 * Gets a Turnstile token by rendering the widget and waiting for completion
 * @returns A Promise that resolves to the token string
 */
export async function getTurnstileToken(): Promise<string> {

    try {
        // First, ensure turnstile is loaded
        await ensureTurnstileLoaded();

        const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
        if (!siteKey) {
            console.error("Missing Turnstile site key");
            return Promise.reject("Turnstile site key is missing.");
        }

        // If a token request is already in progress, return that promise
        if (turnstileTokenPromise) {
            return turnstileTokenPromise;
        }

        // Create a new promise for this token request
        turnstileTokenPromise = new Promise<string>((resolve, reject) => {
            const container = document.querySelector('#turnstile-container');
            if (!container) {
                console.error("Turnstile container not found");
                reject('Turnstile container #turnstile-container not found in the DOM.');
                turnstileTokenPromise = null; // Clear the promise to allow new attempts
                return;
            }


            try {

                // First clear any existing widgets in the container
                while (container.firstChild) {
                    container.removeChild(container.firstChild);
                }

                // For debugging purposes
                const handleError = (error: any) => {
                    console.error("Turnstile error:", error);

                    // Handle specific error codes
                    if (error === 110200) {
                        console.error(
                            "Turnstile Error 110200: Unknown domain - Your domain is not allowed for this Turnstile widget." +
                            "\nPlease verify that your current domain is added to the allowed domains in your Cloudflare dashboard." +
                            "\nIf you're developing locally, you need to add your local domain (e.g., localhost:3000) to your widget configuration." +
                            "\nOr consider using Cloudflare's testing keys for development: 1x00000000000000000000AA"
                        );
                    } else if (error >= 110100 && error <= 110110) {
                        console.error("Turnstile Error: Invalid sitekey - The sitekey is invalid or no longer active.");
                    } else if (error === 110420) {
                        console.error("Turnstile Error: Invalid action parameter format.");
                    } else if (error === 110430) {
                        console.error("Turnstile Error: Invalid cData parameter format.");
                    } else if (error === 110500) {
                        console.error("Turnstile Error: Unsupported browser.");
                    } else if (error >= 110600 && error <= 110699) {
                        console.error("Turnstile Error: Challenge timed out. User took too long to solve the challenge.");
                    }

                    reject('Turnstile challenge failed: ' + error);
                    turnstileTokenPromise = null; // Clear promise to allow new attempt
                };

                const widgetId = window.turnstile.render('#turnstile-container', {
                    sitekey: siteKey,
                    callback: (token: string) => {
                        resolve(token);
                    },
                    'error-callback': handleError,
                    'expired-callback': () => {
                        turnstileTokenPromise = null; // Token expired, clear cache for next call
                    },
                    // Privacy-focused configuration
                    'theme': 'light',
                    'retry': 'auto',
                    'retry-interval': 1000,
                    'refresh-expired': 'auto',
                    // Remove appearance: 'interaction-only'
                });

                // No need for fallback token mechanism since we're using official testing keys
            } catch (error) {
                console.error("Error rendering Turnstile widget:", error);
                reject('Failed to render Turnstile widget: ' + String(error));
                turnstileTokenPromise = null;
            }
        });

        // Handle potential rejection of this specific promise instance to clear the cache
        const currentPromiseInstance = turnstileTokenPromise;
        currentPromiseInstance.catch((error) => {
            console.error("Turnstile promise rejected:", error);
            if (turnstileTokenPromise === currentPromiseInstance) { // Avoid race conditions
                turnstileTokenPromise = null;
            }
        });

        return currentPromiseInstance;
    } catch (error) {
        console.error("Error getting Turnstile token:", error);
        turnstileTokenPromise = null;

        // For development only: return a mock token to allow testing
        if (process.env.APP_ENV !== 'production') {
            return "dev_error_fallback_token";
        }

        return Promise.reject(error instanceof Error ? error.message : String(error));
    }
}

/**
 * Resets the Turnstile widget if it exists
 * @param widgetId Optional widget ID to reset a specific widget
 */
export function resetTurnstileWidget(widgetId?: string | number): void {
    if (typeof window !== 'undefined' && window.turnstile) {
        window.turnstile.reset(widgetId);
        // Also reset our cached promise
        turnstileTokenPromise = null;
    }
}
